// ============================================================================
//  DailyReport - Phase 2: form + save
// ============================================================================
//
//  Auto-fill rows (white, black numbers) come from the read side of
//  /api/daily-report. Manual rows are now real controlled inputs; a
//  single Save button POSTs the whole manual object back to the same
//  endpoint. The server upserts the daily_ops_reports row keyed on date.
//
//  If schema-019 hasn't been run yet, the endpoint's GET response has
//  manualMeta.tableExists = false. The page renders a red banner with
//  the migration hint and disables Save.
// ============================================================================

import { useState, useEffect, useRef } from 'react';
import { useApi, postJson, fetchJson } from '../api.js';

function fmt(n)  { return n == null ? '-' : Number(n).toLocaleString(); }
function fmt2(n) { return n == null ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }); }

function todayLocalIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + da;
}
// Weekday-aware date walker. isoAddWeekdays('2026-07-11', -1) returns
// '2026-07-10' (Friday, skipping past Saturday). Used by the Prev/Next
// buttons and the initial default so operators only see workdays.
function isoAddWeekdays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  let step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  while (remaining > 0) {
    dt.setDate(dt.getDate() + step);
    const day = dt.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;   // 0 = Sun, 6 = Sat
  }
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return yy + '-' + mm + '-' + dd;
}
// Nearest preceding weekday for a given ISO date.
function nearestWeekday(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const day = new Date(y, m - 1, d).getDay();
  if (day === 0) return isoAddWeekdays(iso, -2); // Sun -> Fri
  if (day === 6) return isoAddWeekdays(iso, -1); // Sat -> Fri
  return iso;
}
// Synchronous default (used at mount before /api/daily-report/history
// resolves): today if weekday, else last Friday.
function defaultOpenIso() { return nearestWeekday(todayLocalIso()); }
// Async default: walk backward from today by weekdays and land on the
// first date whose report is NOT locked (edit_locked OR emailed_at).
// Called by an effect once the history endpoint returns lockedDates.
function lastUnlockedWeekday(lockedDates) {
  const lockedSet = new Set(lockedDates || []);
  let cur = nearestWeekday(todayLocalIso());
  // Cap iterations at 2 months of weekdays -- if 40 weekdays back are
  // all locked, something is off and we keep the last one anyway.
  for (let i = 0; i < 40; i++) {
    if (!lockedSet.has(cur)) return cur;
    cur = isoAddWeekdays(cur, -1);
  }
  return cur;
}

// ── Row primitives ─────────────────────────────────────────────────────
function AutoRow({ label, value, unit, hint }) {
  return (
    <tr>
      <td style={{ padding: '0.28rem 0.55rem', color: '#334155' }}>
        {label}
        {hint && <span className="small" style={{ color: '#94a3b8', marginLeft: '0.4rem' }}>{hint}</span>}
      </td>
      <td className="num" style={{ padding: '0.28rem 0.55rem', fontWeight: 600, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </td>
      <td style={{ padding: '0.28rem 0.55rem', color: '#94a3b8', fontSize: '0.8em' }}>
        {unit || ''}
      </td>
    </tr>
  );
}

// Manual controlled input row. `type` is 'text' | 'num' | 'int'. Fully
// re-uses the parent's manual state via getter/setter props.
//
// 2026-07-09: touch-friendlier inputs for mobile plant walkabout use.
// - font-size 16px minimum (iOS otherwise auto-zooms on focus)
// - min-height 32px so fingertips can hit them
// - number inputs get inputMode="decimal" so mobile shows the numeric
//   keypad instead of the full QWERTY. Suresh 2026-07-09.
function ManualInput({ label, unit, name, type, manual, setManual, textarea, disabled }) {
  const val = manual[name] == null ? '' : manual[name];
  const inputType = type === 'text' ? 'text' : 'number';
  const step = type === 'num' ? 'any' : (type === 'int' ? '1' : undefined);
  const inputMode = type === 'int' ? 'numeric' : (type === 'num' ? 'decimal' : undefined);
  const onChange = (e) => setManual(m => ({ ...m, [name]: e.target.value }));
  const inputStyle = {
    width: '100%', fontSize: '16px', padding: '6px 8px',
    border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box',
    minHeight: 32, textAlign: type === 'text' ? 'left' : 'right',
    background: disabled ? '#f1f5f9' : '#fff'
  };
  // Textareas span all three columns of the section (label + value +
  // unit) with the label rendered inline-left of the textarea via
  // flexbox and a fixed label width. Gives the writing area ~all of
  // the row width instead of just the ~9em value column. Numeric /
  // short-text rows still use the tight 3-column layout so numbers
  // line up neatly across rows.
  return (
    <tr>
      {textarea ? (
        <td colSpan={3} style={{ padding: '0.3rem 0.55rem', borderLeft: '3px solid #cbd5e1' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <div style={{ minWidth: 160, maxWidth: 200, color: '#0f172a',
                          paddingTop: '0.35rem', flexShrink: 0 }}>
              {label}
            </div>
            <textarea rows={3} value={val} onChange={onChange} disabled={disabled}
                      style={{ ...inputStyle, textAlign: 'left', minHeight: 64, resize: 'vertical', flex: 1 }} />
          </div>
        </td>
      ) : (
        <>
          <td style={{ padding: '0.4rem 0.55rem', color: '#0f172a', borderLeft: '3px solid #cbd5e1' }}>
            {label}
          </td>
          <td style={{ padding: '0.2rem 0.55rem' }}>
            <input type={inputType} step={step} inputMode={inputMode} value={val}
                   onChange={onChange} disabled={disabled} style={inputStyle} />
          </td>
          <td style={{ padding: '0.4rem 0.55rem', color: '#94a3b8', fontSize: '0.8em', whiteSpace: 'nowrap' }}>
            {unit || ''}
          </td>
        </>
      )}
    </tr>
  );
}

// RichNoteRow -- <tr> row for a narrative note field with a small
// rich-text editor (bold / italic / underline / bullets / numbered
// list). Uses a contenteditable div + document.execCommand for the
// zero-dependency implementation. Output is HTML stored in
// manual[name]; the PDF renderer parses the tags and preserves the
// formatting. Suresh 2026-07-10.
function RichNoteRow({ label, name, manual, setManual, disabled }) {
  const val = manual[name] == null ? '' : String(manual[name]);
  const ref = useRef(null);
  // Sync from parent state to the contenteditable ONLY when the value
  // arrives from outside (initial mount, date-change reload, saved
  // response). Skip when the user is typing to avoid caret-jump.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== val && document.activeElement !== ref.current) {
      ref.current.innerHTML = val;
    }
  }, [val]);
  const exec = (cmd) => {
    if (disabled) return;
    document.execCommand(cmd, false, null);
    if (ref.current) setManual(m => ({ ...m, [name]: ref.current.innerHTML }));
  };
  const onInput = () => {
    if (ref.current) setManual(m => ({ ...m, [name]: ref.current.innerHTML }));
  };
  const toolBtnStyle = {
    minWidth: 28, height: 26, padding: '0 6px',
    background: disabled ? '#f1f5f9' : '#fff',
    border: '1px solid #cbd5e1', borderRadius: 3,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, color: '#0f172a'
  };
  return (
    <tr>
      <td colSpan={3} style={{ padding: '0.3rem 0.55rem', borderLeft: '3px solid #cbd5e1' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ minWidth: 160, maxWidth: 200, color: '#0f172a',
                        paddingTop: '0.35rem', flexShrink: 0 }}>
            {label}
          </div>
          <div style={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 4,
                        background: disabled ? '#f1f5f9' : '#fff', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 4, padding: '4px 6px',
                          borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
              <button type="button" onMouseDown={e => e.preventDefault()}
                      onClick={() => exec('bold')} disabled={disabled}
                      title="Bold" style={{ ...toolBtnStyle, fontWeight: 700 }}>B</button>
              <button type="button" onMouseDown={e => e.preventDefault()}
                      onClick={() => exec('italic')} disabled={disabled}
                      title="Italic" style={{ ...toolBtnStyle, fontStyle: 'italic' }}>I</button>
              <button type="button" onMouseDown={e => e.preventDefault()}
                      onClick={() => exec('underline')} disabled={disabled}
                      title="Underline" style={{ ...toolBtnStyle, textDecoration: 'underline' }}>U</button>
              <span style={{ width: 1, background: '#e5e7eb', margin: '0 4px' }} />
              <button type="button" onMouseDown={e => e.preventDefault()}
                      onClick={() => exec('insertUnorderedList')} disabled={disabled}
                      title="Bullet list" style={toolBtnStyle}>&bull;</button>
              <button type="button" onMouseDown={e => e.preventDefault()}
                      onClick={() => exec('insertOrderedList')} disabled={disabled}
                      title="Numbered list" style={toolBtnStyle}>1.</button>
            </div>
            <div ref={ref} contentEditable={!disabled} onInput={onInput}
                 suppressContentEditableWarning
                 style={{ padding: '8px 10px', minHeight: 68, outline: 'none',
                          fontSize: 14, lineHeight: 1.4, color: '#0f172a',
                          textAlign: 'left' }} />
          </div>
        </div>
      </td>
    </tr>
  );
}

// EquipmentInput -- one input per registered equipment/vehicle. Writes
// to manual[e.source_column] if the equipment still has a legacy
// source_column (the four seeded rows), otherwise into
// manual.equipment_readings[e.code] jsonb. That way custom items like
// Truck / Pickup that never had a fixed column still round-trip through
// the daily_ops_reports table without needing new columns per gear.
function EquipmentInput({ eq, unit, manual, setManual, disabled }) {
  const useSrc = !!(eq.source_column && String(eq.source_column).trim());
  const val = useSrc
    ? (manual[eq.source_column] == null ? '' : manual[eq.source_column])
    : (manual.equipment_readings && manual.equipment_readings[eq.code] != null
        ? manual.equipment_readings[eq.code]
        : '');
  const onChange = (e) => {
    const v = e.target.value;
    if (useSrc) {
      setManual(m => ({ ...m, [eq.source_column]: v }));
    } else {
      setManual(m => ({
        ...m,
        equipment_readings: { ...(m.equipment_readings || {}), [eq.code]: v }
      }));
    }
  };
  return (
    <tr>
      <td style={{ padding: '0.4rem 0.55rem', color: '#0f172a', borderLeft: '3px solid #cbd5e1' }}>
        {eq.display_name}
      </td>
      <td style={{ padding: '0.2rem 0.55rem' }}>
        <input type="number" step="any" inputMode="decimal" value={val}
               onChange={onChange} disabled={disabled}
               style={{ width: '100%', fontSize: '16px', padding: '6px 8px',
                        border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box',
                        minHeight: 32, textAlign: 'right',
                        background: disabled ? '#f1f5f9' : '#fff' }} />
      </td>
      <td style={{ padding: '0.4rem 0.55rem', color: '#94a3b8', fontSize: '0.8em', whiteSpace: 'nowrap' }}>
        {unit}
      </td>
    </tr>
  );
}

// MeterInput (2026-10-14) -- the operator types the CLOSING METER READING
// (town water meter, equipment hour meters). The day's usage = this reading
// minus the previous saved reading; the server stores it in the usual usage
// column (town_water_m3, loader_hours ...). Shown live here so the operator
// can catch a typo before saving.
function MeterInput({ meterKey, label, unit, meters, manual, setManual, disabled }) {
  const readings = manual.meter_readings || {};
  const val = readings[meterKey] == null ? '' : readings[meterKey];
  const info = (meters && meters[meterKey]) || {};
  const prev = info.prev || null;
  const n = val === '' ? null : Number(val);
  const usage = n != null && Number.isFinite(n) && prev ? Math.round((n - prev.value) * 100) / 100 : null;
  const bad = usage != null && usage < 0;
  const onChange = (e) => {
    const v = e.target.value;
    setManual(m => ({ ...m, meter_readings: { ...(m.meter_readings || {}), [meterKey]: v } }));
  };
  return (
    <tr>
      <td style={{ padding: '0.4rem 0.55rem', color: '#0f172a', borderLeft: '3px solid #0ea5e9' }}>
        {label}
        <div className="small" style={{ color: '#64748b' }}>
          {prev ? 'Previous reading ' + prev.value.toLocaleString() + ' on ' + prev.date : 'No earlier reading on file'}
        </div>
        {usage != null && (
          <div style={{ marginTop: 2, fontWeight: 700, color: bad ? '#b91c1c' : '#15803d' }}>
            {bad ? 'Lower than the previous reading — check the meter'
                 : 'Today: ' + usage.toLocaleString() + ' ' + unit}
          </div>
        )}
      </td>
      <td style={{ padding: '0.2rem 0.55rem' }}>
        <input type="number" step="any" inputMode="decimal" value={val} placeholder="closing reading"
               onChange={onChange} disabled={disabled}
               style={{ width: '100%', fontSize: '16px', padding: '6px 8px',
                        border: '1px solid ' + (bad ? '#dc2626' : '#cbd5e1'), borderRadius: 4, boxSizing: 'border-box',
                        minHeight: 32, textAlign: 'right', background: disabled ? '#f1f5f9' : '#fff' }} />
      </td>
      <td style={{ padding: '0.4rem 0.55rem', color: '#94a3b8', fontSize: '0.8em', whiteSpace: 'nowrap' }}>
        {unit === 'km' ? 'odometer' : unit + ' meter'}
      </td>
    </tr>
  );
}

// ShiftSelector -- multi-select of active employees for a shift field.
// Trigger button shows the selected initials in "JD, MK" format (same
// text the backend already parses via parse_staff_initials). Click to
// open a dropdown of checkboxes, each labeled with the employee's full
// name plus initials. Closes on outside click or Escape.
// Suresh 2026-07-09.
function ShiftSelector({ label, name, employees, manual, setManual, disabled }) {
  const raw = manual[name] == null ? '' : String(manual[name]);
  const selected = new Set(
    raw.split(/[,\.\s\/]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
  );
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);   // trigger rect for fixed-position dropdown
  const wrapRef = useRef(null);
  const btnRef  = useRef(null);
  const dropdownRef = useRef(null);

  // Recompute dropdown rect when opened. Fixed positioning + rect
  // computation lets the dropdown escape any ancestor with
  // overflow:hidden (which every Section has, for rounded-corner
  // clipping). Also re-runs on scroll and resize while open.
  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setRect({ left: r.left, top: r.bottom + 4, width: r.width });
      }
    };
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    const onDoc = (ev) => {
      const inTrigger = wrapRef.current && wrapRef.current.contains(ev.target);
      const inDrop    = dropdownRef.current && dropdownRef.current.contains(ev.target);
      if (!inTrigger && !inDrop) setOpen(false);
    };
    const onKey = (ev) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (initials) => {
    const next = new Set(selected);
    if (next.has(initials)) next.delete(initials);
    else next.add(initials);
    // Preserve roster order so "JD, MK" stays stable across toggles.
    const rosterOrder = employees.map(e => e.initials);
    const ordered = rosterOrder.filter(i => next.has(i));
    setManual(m => ({ ...m, [name]: ordered.join(', ') }));
  };

  // Every shift dropdown lists every employee -- active first, then
  // inactive sorted to the bottom (still selectable but visually
  // dimmed). If an initials value was saved for someone who has since
  // been removed from the roster, we still want them clickable, so we
  // synthesise a placeholder row for anyone in `selected` but not in
  // the employees list.
  const byInitials = new Map(employees.map(e => [e.initials, e]));
  for (const init of selected) {
    if (!byInitials.has(init)) {
      byInitials.set(init, { id: 'x-' + init, initials: init, first_name: '', last_name: '', active: false });
    }
  }
  const rosterAll = Array.from(byInitials.values()).sort((a, b) => {
    // Active first, then alphabetic by full name (falls back to initials).
    if ((a.active !== false) !== (b.active !== false)) return (a.active !== false) ? -1 : 1;
    const an = ((a.first_name || '') + ' ' + (a.last_name || '')).trim() || a.initials;
    const bn = ((b.first_name || '') + ' ' + (b.last_name || '')).trim() || b.initials;
    return an.localeCompare(bn);
  });
  const summary = raw.trim() || 'Select staff...';

  return (
    <tr>
      <td style={{ padding: '0.4rem 0.55rem', color: '#0f172a', borderLeft: '3px solid #cbd5e1', verticalAlign: 'top' }}>
        {label}
      </td>
      <td style={{ padding: '0.3rem 0.55rem' }} colSpan={2}>
        {rosterAll.length === 0 && (
          <div className="small" style={{ color: '#94a3b8' }}>No employees on file. Add some via Reference &rarr; Employees.</div>
        )}
        <div ref={wrapRef} style={{ position: 'relative' }}>
          <button ref={btnRef} type="button" disabled={rosterAll.length === 0}
                  onClick={() => setOpen(o => !o)}
                  title={disabled ? 'Locked - open to view the roster (checkboxes are read-only)' : ''}
                  style={{
                    width: '100%', minHeight: 36, padding: '6px 30px 6px 10px',
                    borderRadius: 4, border: '1px solid ' + (disabled ? '#e5e7eb' : '#cbd5e1'),
                    background: disabled ? '#f8fafc' : '#fff',
                    color: raw ? '#0f172a' : '#94a3b8',
                    textAlign: 'left', fontWeight: raw ? 600 : 400,
                    fontSize: '0.95em', cursor: 'pointer',
                    position: 'relative'
                  }}>
            {summary}
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
              {open ? '▲' : '▼'}
            </span>
          </button>
          {open && rect && (
            <div ref={dropdownRef} style={{
              position: 'fixed', top: rect.top, left: rect.left, width: rect.width,
              zIndex: 9999,
              background: '#fff', border: '1px solid #cbd5e1',
              borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxHeight: 280, overflowY: 'auto'
            }}>
              {disabled && (
                <div className="small" style={{
                  padding: '6px 10px', color: '#78350f', background: '#fef3c7',
                  borderBottom: '1px solid #fde68a', fontWeight: 600
                }}>Locked - view only</div>
              )}
              {rosterAll.map(e => {
                const on = selected.has(e.initials);
                const full = ((e.first_name || '') + ' ' + (e.last_name || '')).trim() || e.initials;
                const inactive = e.active === false;
                return (
                  <label key={e.id} style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '8px 10px', cursor: disabled ? 'default' : 'pointer',
                    borderBottom: '1px solid #f1f5f9',
                    background: on ? '#eff6ff' : '#fff',
                    color: inactive ? '#94a3b8' : 'inherit'
                  }}>
                    <input type="checkbox" checked={on} disabled={disabled}
                           onChange={() => !disabled && toggle(e.initials)}
                           style={{ margin: 0 }} />
                    <span style={{ flex: 1 }}>{full}{inactive && ' (inactive)'}</span>
                    <span className="small" style={{ color: '#64748b', fontFamily: 'monospace' }}>{e.initials}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        {/* Raw-text override so ops can type in a name that isn't in the roster yet. */}
        <input type="text" value={raw} onChange={e => setManual(m => ({ ...m, [name]: e.target.value }))}
          disabled={disabled} placeholder="e.g. JD, MK"
          style={{ width: '100%', fontSize: '14px', marginTop: '0.35rem',
                   padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 3,
                   color: '#64748b', background: disabled ? '#f1f5f9' : '#fafafa' }} />
      </td>
    </tr>
  );
}

function Section({ title, tint, children, right }) {
  return (
    <div style={{
      marginBottom: '0.8rem',
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '0.45rem 0.8rem',
        background: tint || '#f1f5f9',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '0.5rem'
      }}>
        <h3 style={{ margin: 0, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#334155' }}>
          {title}
        </h3>
        {right && <div className="small" style={{ color: '#64748b' }}>{right}</div>}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
        <colgroup>
          <col style={{ width: 'auto' }} />
          <col style={{ width: '9em' }} />
          <col style={{ width: '6em' }} />
        </colgroup>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function TwoUp({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '0.6rem' }}>
      {children}
    </div>
  );
}

export default function DailyReport() {
  const [date, setDate] = useState(defaultOpenIso());
  // Fetch current user so we can gate the admin-only Unlock button.
  const { data: meData } = useApi('/api/whoami');
  const me = meData || null;
  const { data, error, loading, refresh } = useApi('/api/daily-report?date=' + date);
  // Employee list for the shift pickers. Fetch ALL employees so every
  // dropdown shows the full roster regardless of active flag; the flag
  // is only used to sort inactive ones to the bottom. Suresh 2026-07-09.
  const { data: employeesData } = useApi('/api/employees');
  const employees = (employeesData && employeesData.employees) || [];
  // History: list of saved dates + navigator.
  const { data: histData, refresh: refreshHist } = useApi('/api/daily-report/history');
  // Active equipment + vehicles from the Reference tab. Each active row
  // gets its own input on this page so ops can log daily hours (or km)
  // for anything the site owner has registered. New adds show up
  // automatically -- no code change needed.
  const { data: equipData } = useApi('/api/equipment');
  const equipmentList = ((equipData && equipData.equipment) || []).filter(e => e.active !== false);
  const savedDates = (histData && histData.dates) || [];
  const lockedDates = (histData && histData.lockedDates) || [];
  // On first load, jump to the most recent unlocked weekday once the
  // history endpoint returns. Guarded so subsequent nav via Prev/Next
  // or History doesn't get overridden. Suresh 2026-07-10.
  const didAutoLandRef = useRef(false);
  useEffect(() => {
    if (didAutoLandRef.current) return;
    if (!histData || !Array.isArray(histData.lockedDates)) return;
    const target = lastUnlockedWeekday(histData.lockedDates);
    didAutoLandRef.current = true;
    if (target && target !== date) setDate(target);
  }, [histData]);
  // Import modal state.
  const [importOpen, setImportOpen] = useState(false);

  // Manual entry state — keyed by column name from MANUAL_FIELDS on the backend.
  const [manual, setManual] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // Reset form state immediately when the user navigates to a different
  // date -- otherwise the previous day's staff/hours flash in until the
  // new day's API response arrives, and if they hit Save before the new
  // data lands they'd overwrite the new date with the old day's values.
  useEffect(() => {
    setManual({});
    setSaveMsg(null);
  }, [date]);

  // Pre-populate manual state whenever the loaded row changes for this date.
  // Also merge Phase 3a's suggestedWeather (from Open-Meteo) into any
  // still-empty weather fields — never overwrites what ops actually typed
  // and was saved.
  useEffect(() => {
    if (data && data.manual) {
      const m = { ...data.manual };
      // strip the metadata columns
      delete m.submitted_by; delete m.submitted_at;
      delete m.updated_by;   delete m.updated_at;
      const w = data.suggestedWeather;
      if (w) {
        if ((m.temp_max == null || m.temp_max === '') && w.tempMax != null) m.temp_max = w.tempMax;
        if ((m.temp_min == null || m.temp_min === '') && w.tempMin != null) m.temp_min = w.tempMin;
        if ((m.weather  == null || m.weather  === '') && w.summary != null) m.weather  = w.summary;
      }
      setManual(m);
      setSaveMsg(null);
    }
  }, [data && data.date, data && data.manual && data.manual.updated_at,
      data && data.suggestedWeather && data.suggestedWeather.summary]);

  if (loading && !data) return <div className="loading">Loading daily report...</div>;
  if (error)             return <div className="error">Error loading daily report: {error}</div>;
  if (!data)             return null;

  const inbound   = data.inbound || {};
  const buckets   = inbound.hourlyBuckets || {};
  const badger    = data.badger || {};
  const outbound  = data.outbound || {};
  const clay      = outbound.clay || {};
  const sand      = outbound.sand || {};
  const totalTons = outbound.totalTons || {};
  const totalTrucks = outbound.totalTrucks || {};
  const diag      = outbound.diag || {};
  const meta      = data.manualMeta || {};
  const tableMissing = meta.tableExists === false;
  const hardLocked = !!data.hardLocked;
  const editLocked = !!data.editLocked || hardLocked;
  // Every input on the page respects this. tableMissing already blocked
  // writes on fresh envs; lock now blocks writes on prior + emailed days.
  const locked = editLocked || tableMissing;
  const suggWeather = data.suggestedWeather || null;
  const wxDebug     = data.weatherDebug || null;
  const tickets     = data.tickets || { balances: [], tableExists: true };

  // Every visible input field must have SOMETHING in it before Save.
  // "0" and "-" are valid values; empty string / null / undefined is not.
  // Labels here mirror the on-screen ManualInput label prop so the
  // "Missing:" list is readable.
  const REQUIRED_FIELDS = [
    { name: 'weather',                label: 'Weather' },
    { name: 'temp_max',               label: 'Max temp' },
    { name: 'temp_min',               label: 'Min temp' },
    { name: 'staff_morning',          label: 'Staff - morning' },
    { name: 'staff_afternoon',        label: 'Staff - afternoon' },
    // staff_night intentionally omitted -- night shift is optional.
    { name: 'drums_in',               label: 'Drum intake - day' },
    { name: 'recirc_pct',             label: 'Recirc tank level' },
    { name: 'thickener_torque_pct',   label: 'Thickener torque' },
    { name: 'sludge_pct',             label: 'Sludge tank level' },
    { name: 'process_water_pct',      label: 'Process water tank level' },
    { name: 'coagulant_prod_pct',     label: 'Coagulant tote in prod' },
    { name: 'coagulant_warehouse_qty',label: 'Warehouse coagulant totes' },
    { name: 'polymer_tank_pct',       label: 'Polymer storage tank' },
    { name: 'polymer_warehouse_bags', label: 'Warehouse polymer bags' },
    { name: 'polymer_cationic_containers', label: 'New cationic polymer' },
    { name: 'water_loads',            label: 'Water loads' },
    { name: 'po_status',              label: 'Purchasing / PO status' },
    { name: 'centrifuge_notes',       label: 'Centrifuge notes' },
    { name: 'other_notes',            label: 'Other notes' }
  ];
  // Strip HTML tags for the emptiness check so rich-text notes that
  // contain only "<p><br></p>" or similar wrapper markup still count
  // as missing.
  function isEmptyValue(v) {
    if (v == null) return true;
    const s = String(v).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return s === '';
  }
  function findMissing() {
    const out = [];
    for (const f of REQUIRED_FIELDS) {
      if (isEmptyValue(manual[f.name])) out.push(f.label);
    }
    // Closing meter readings (town water + metered equipment).
    const mr = manual.meter_readings || {};
    if (isEmptyValue(mr.TOWN_WATER)) out.push('Town water meter reading');
    for (const eq of equipmentList) {
      if (eq.metered && isEmptyValue(mr[eq.code])) out.push(eq.display_name + (eq.kind === 'vehicles' ? ' (odometer)' : ' (meter reading)'));
    }
    // Plus every active equipment/vehicle input on the page.
    for (const eq of equipmentList) {
      if (eq.metered || !eq.source_column) continue;
      const v = manual[eq.source_column];
      if (v == null || String(v).trim() === '') {
        out.push(eq.display_name + (eq.kind === 'vehicles' ? ' (km)' : ' (hrs)'));
      }
    }
    return out;
  }

  // Save = persist only. Lock = persist + freeze (no email). Send =
  // persist + email + freeze. All three write via the same POST; the
  // `lock` and `email` flags tell the backend which extras to run.
  async function save(opts = {}) {
    if (tableMissing) {
      setSaveMsg({ kind: 'error', text: 'daily_ops_reports table not yet created in Supabase.' });
      return;
    }
    // Partial saves are allowed (Suresh 2026-10-14); only Send & Lock and
    // Lock need every field, since those freeze the report.
    const missing = findMissing();
    if (missing.length > 0 && (opts.email || opts.lock)) {
      setSaveMsg({ kind: 'error',
        text: 'Fill every field before ' + (opts.email ? 'sending' : 'locking') + ' (0 or - is fine). Missing: ' + missing.join(', ') });
      return;
    }
    setSaving(true); setSaveMsg(null);
    try {
      const payload = { date, ...manual, email: !!opts.email, lock: !!opts.lock };
      const r = await postJson('/api/daily-report', payload);
      refresh();
      if (opts.email) {
        const sent = r && r.emailSent;
        if (sent && sent.error) {
          setSaveMsg({ kind: 'error', text: 'Saved, but email failed: ' + sent.error });
        } else if (sent && Array.isArray(sent.to)) {
          setSaveMsg({ kind: 'ok', text: 'Sent to ' + sent.to.length + ' recipient(s). Report is now locked.' });
        } else {
          setSaveMsg({ kind: 'ok', text: 'Saved and sent.' });
        }
      } else if (opts.lock) {
        setSaveMsg({ kind: 'ok', text: 'Saved and locked. Report is frozen -- no further edits.' });
      } else {
        setSaveMsg({ kind: 'ok', text: 'Saved ' + date + '.' + (missing.length
          ? ' Still to fill before sending: ' + missing.join(', ') + '.' : '') });
      }
    } catch (e) {
      let msg = e.message || String(e);
      if (msg.includes('503')) msg = 'Save failed - daily_ops_reports table missing. Run schema-019 against Supabase.';
      if (msg.includes('423')) msg = 'This report is locked and can no longer be edited.';
      setSaveMsg({ kind: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  }
  async function send() {
    if (!confirm(
      'Send the Daily Ops Report for ' + date + '?\n\n' +
      'This will:\n' +
      '  1. Save the current values.\n' +
      '  2. Email the PDF to all configured recipients.\n' +
      '  3. LOCK the report -- no further edits possible even if upstream data changes.\n\n' +
      'Proceed?'
    )) return;
    await save({ email: true, lock: true });
  }
  async function lockNoSend() {
    if (!confirm(
      'Lock the Daily Ops Report for ' + date + ' WITHOUT sending?\n\n' +
      'This will:\n' +
      '  1. Save the current values.\n' +
      '  2. Freeze the report -- no further edits, no email.\n' +
      '  3. Upstream tips / loads changes will not mutate what is frozen.\n\n' +
      'Proceed?'
    )) return;
    await save({ lock: true });
  }
  // Admin-only. Clears edit_locked / locked_snapshot_jsonb / emailed_at
  // so the day becomes fully editable again. Backend enforces the admin
  // check as well; the UI just hides the button for non-admins.
  // Force-refetch Open-Meteo for the current date and overwrite the
  // weather / temp fields in local state. Doesn't Save -- the operator
  // can review and hit Save when happy. Used when the saved values
  // are garbage (e.g. 0.0 temps) and the normal auto-fill skipped
  // because "saved row already has weather values".
  async function refreshWeather() {
    setSaveMsg(null);
    try {
      const fresh = await fetchJson('/api/daily-report?date=' + date + '&forceWeather=1');
      const w = fresh && fresh.suggestedWeather;
      if (!w) {
        setSaveMsg({ kind: 'error', text: 'Weather refresh failed: ' + ((fresh && fresh.weatherDebug && fresh.weatherDebug.reason) || 'no data') });
        return;
      }
      setManual(m => ({
        ...m,
        weather:  w.summary != null ? w.summary  : m.weather,
        temp_max: w.tempMax != null ? w.tempMax  : m.temp_max,
        temp_min: w.tempMin != null ? w.tempMin  : m.temp_min
      }));
      setSaveMsg({ kind: 'ok', text: 'Weather refreshed from Open-Meteo. Click Save to keep.' });
    } catch (e) {
      setSaveMsg({ kind: 'error', text: 'Weather refresh failed: ' + (e.message || String(e)) });
    }
  }

  async function unlock() {
    if (!confirm(
      'Unlock the Daily Ops Report for ' + date + '?\n\n' +
      'The frozen snapshot will be discarded and inbound / outbound\n' +
      'aggregates will be re-computed live. Emailed_at will be cleared.\n\n' +
      'This is an admin action -- proceed?'
    )) return;
    setSaving(true); setSaveMsg(null);
    try {
      await postJson('/api/daily-report', { date, unlock: true });
      refresh();
      setSaveMsg({ kind: 'ok', text: 'Unlocked. Report is editable again.' });
    } catch (e) {
      setSaveMsg({ kind: 'error', text: e.message || String(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page dailyops">
      <div className="dailyops-head">
        <div className="dailyops-title">
          <h1 style={{ margin: 0, fontSize: '1.35rem', lineHeight: 1.2 }}>Daily Ops Report</h1>
          <div style={{ fontSize: '0.95em', color: '#334155', marginTop: '0.15rem', fontWeight: 500 }}>
            {data.dayOfWeek}, {data.date}
          </div>
          {meta.exists && data.manual && data.manual.updated_at && (
            <div className="small" style={{ color: '#64748b', marginTop: '0.1rem' }}>
              Saved {String(data.manual.updated_at).slice(0, 16).replace('T', ' ')} by {data.manual.updated_by || '?'}
            </div>
          )}
        </div>
        <div className="dailyops-nav">
          <button className="refresh-btn dailyops-navbtn" type="button"
                  onClick={() => setDate(isoAddWeekdays(date, -1))}
                  title="Previous weekday">&laquo;</button>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 className="dailyops-datepick" />
          <button className="refresh-btn dailyops-navbtn" type="button"
                  onClick={() => setDate(isoAddWeekdays(date, 1))}
                  title="Next weekday">&raquo;</button>
          {savedDates.length > 0 && (
            <select className="refresh-btn dailyops-history"
                    value={savedDates.includes(date) ? date : ''}
                    onChange={e => e.target.value && setDate(e.target.value)}
                    title="Jump to a saved date">
              <option value="">History</option>
              {savedDates.slice(0, 200).map(d => (<option key={d} value={d}>{d}</option>))}
            </select>
          )}
        </div>
        <div className="dailyops-actions">
          <button className="refresh-btn" onClick={refresh} disabled={saving}>Reload</button>
          <button className="refresh-btn dailyops-save" onClick={() => save()} disabled={saving || locked}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="refresh-btn dailyops-lock" onClick={lockNoSend} disabled={saving || locked}
                  title="Save and lock without sending">Lock</button>
          <button className="refresh-btn dailyops-send" onClick={send} disabled={saving || locked}
                  title="Save, email PDF to recipients, and lock">Send &amp; Lock</button>
        </div>
      </div>

      {/* Mobile-only sticky save button. Fixed to the bottom of the viewport
          via .dailyops-save-sticky in styles.css so the operator can save
          without scrolling to the top on a phone. Hidden on desktop. */}
      <button className="dailyops-save-sticky" onClick={() => save()}
              disabled={saving || locked}
              type="button">
        {saving ? 'Saving...' : (saveMsg && saveMsg.kind === 'ok' ? 'Saved -- tap to save again' : 'Save daily report')}
      </button>

      {tableMissing && (
        <div className="card" style={{ padding: '0.5rem 0.8rem', marginBottom: '0.5rem',
                                        background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
          <strong>daily_ops_reports table not yet created.</strong> Save is disabled until schema-019 is applied.
          {meta.hint && <div className="small" style={{ marginTop: '0.2rem', color: '#7f1d1d' }}>{meta.hint}</div>}
        </div>
      )}
      {hardLocked && data.emailedAt && (
        <div className="card" style={{ padding: '0.5rem 0.8rem', marginBottom: '0.5rem',
                                        background: '#fef2f2', color: '#7f1d1d', border: '1px solid #fecaca',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
          <span>
            <strong>&#128274; This report has been sent</strong> ({String(data.emailedAt).slice(0, 19).replace('T',' ')} UTC).
            {' '}Aggregates + manual fields are frozen -- upstream inbound/outbound edits won't change what was sent.
          </span>
          {me && me.isAdmin && (
            <button className="refresh-btn" onClick={unlock} disabled={saving}
                    style={{ background: '#7f1d1d', color: '#fff', flexShrink: 0 }}>
              Unlock (admin)
            </button>
          )}
        </div>
      )}
      {hardLocked && !data.emailedAt && (
        <div className="card" style={{ padding: '0.5rem 0.8rem', marginBottom: '0.5rem',
                                        background: '#fef2f2', color: '#7f1d1d', border: '1px solid #fecaca',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
          <span>
            <strong>&#128274; This report is locked.</strong> Values are frozen -- upstream inbound/outbound edits will not change them.
          </span>
          {me && me.isAdmin && (
            <button className="refresh-btn" onClick={unlock} disabled={saving}
                    style={{ background: '#7f1d1d', color: '#fff', flexShrink: 0 }}>
              Unlock (admin)
            </button>
          )}
        </div>
      )}
      {!hardLocked && editLocked && (
        <div className="card" style={{ padding: '0.5rem 0.8rem', marginBottom: '0.5rem',
                                        background: '#fef3c7', color: '#78350f', border: '1px solid #fde68a',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
          <span>
            <strong>&#128274; Prior day -- read only.</strong> Historical reports are locked to preserve the record.
            Ask an admin if this day needs correcting.
          </span>
          {me && me.isAdmin && (
            <button className="refresh-btn" onClick={unlock} disabled={saving}
                    style={{ background: '#78350f', color: '#fff', flexShrink: 0 }}>
              Unlock (admin)
            </button>
          )}
        </div>
      )}
      {saveMsg && (
        <div className="card" style={{ padding: '0.5rem 0.8rem', marginBottom: '0.5rem',
                                        background: saveMsg.kind === 'ok' ? '#dcfce7' : '#fee2e2',
                                        color:      saveMsg.kind === 'ok' ? '#166534' : '#991b1b' }}>
          {saveMsg.text}
        </div>
      )}

      <Section title="Header context" tint="#fef9c3"
               right={
                 <div style={{ textAlign: 'right' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end' }}>
                     {suggWeather
                       ? <span>Weather pre-filled from Open-Meteo</span>
                       : (wxDebug && !wxDebug.ok
                           ? <span style={{ color: '#b91c1c' }}>Weather: {wxDebug.reason || 'unknown'}</span>
                           : null)}
                     <button className="refresh-btn" type="button"
                             onClick={refreshWeather}
                             disabled={locked}
                             title="Force-refetch weather from Open-Meteo and overwrite the current values"
                             style={{ padding: '2px 8px', fontSize: '0.85em' }}>
                       &#8635; Refresh weather
                     </button>
                   </div>
                   {data.plantLocation && (() => {
                     const lat = Number(data.plantLocation.lat);
                     const lon = Number(data.plantLocation.lon);
                     const mapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;
                     return (
                       <div style={{ fontSize: '0.85em', color: '#64748b', marginTop: '0.15rem' }}>
                         Location:{' '}
                         {data.plantLocation.address && <>{data.plantLocation.address}<br/></>}
                         <span style={{ fontFamily: 'monospace' }}>
                           {lat.toFixed(4)}, {lon.toFixed(4)}
                         </span>
                         {' '}
                         <a href={mapUrl} target="_blank" rel="noreferrer"
                            style={{ color: '#0369a1', textDecoration: 'underline' }}>
                           verify on map
                         </a>
                         {data.plantLocation.source === 'env_default' && ' (default - set in Reference)'}
                       </div>
                     );
                   })()}
                 </div>
               }>
        <AutoRow     label="Date"        value={data.date} />
        <AutoRow     label="Day of week" value={data.dayOfWeek} />
        <ManualInput label="Weather"           name="weather"         type="text" manual={manual} setManual={setManual} disabled={locked} />
        <ManualInput label="Max temp"          name="temp_max"        type="num"  unit="C" manual={manual} setManual={setManual} disabled={locked} />
        <ManualInput label="Min temp"          name="temp_min"        type="num"  unit="C" manual={manual} setManual={setManual} disabled={locked} />
        <ShiftSelector label="Staff - morning"   name="staff_morning"   employees={employees} manual={manual} setManual={setManual} disabled={locked} />
        <ShiftSelector label="Staff - afternoon" name="staff_afternoon" employees={employees} manual={manual} setManual={setManual} disabled={locked} />
        <ShiftSelector label="Staff - night"     name="staff_night"     employees={employees} manual={manual} setManual={setManual} disabled={locked} />
      </Section>

      <TwoUp>
        <Section title="Inbound truck info" tint="#dbeafe">
          <AutoRow label="6 am - 11 am"  value={fmt(buckets['6to11'])}  unit="loads" />
          <AutoRow label="11 am - 2 pm"  value={fmt(buckets['11to14'])} unit="loads" />
          <AutoRow label="2 pm - 4 pm"   value={fmt(buckets['14to16'])} unit="loads" />
          <AutoRow label="4 pm - 6 pm"   value={fmt(buckets['16to18'])} unit="loads" />
          <AutoRow label="6 pm - 9 pm"   value={fmt(buckets['18to21'])} unit="loads" />
          <AutoRow label="9 pm - 6 am"   value={fmt(buckets['21to6'])}  unit="loads" />
          <tr><td colSpan={3} style={{ borderTop: '1px solid #e5e7eb' }}></td></tr>
          <AutoRow label="Total trucks today"  value={fmt(inbound.dayTotal)}    unit="loads" />
          <AutoRow label="Month to date"        value={fmt(inbound.monthToDate)} unit="loads" />
          <AutoRow label="Year to date"         value={fmt(inbound.yearToDate)}  unit="loads"
                   hint={"FY " + (data.periods && data.periods.fyStart)} />
          <ManualInput label="Drum intake"       name="drums_in"      type="int" unit="ea" manual={manual} setManual={setManual} disabled={locked} />
          <MeterInput  label="Town water meter (closing reading)" meterKey="TOWN_WATER" unit="m3" meters={data.meters} manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Water loads"       name="water_loads"   type="int" unit="loads" manual={manual} setManual={setManual} disabled={locked} />
          <tr><td colSpan={3} style={{ borderTop: '1px solid #e5e7eb' }}></td></tr>
          <AutoRow label="Badger today"      value={fmt(badger.daily)}         unit="loads" />
          <AutoRow label="Badger MTD"      value={fmt(badger.mtd)}           unit="loads" />
          <AutoRow label="Badger avg/day"  value={fmt2(badger.mtdAvgPerDay)} unit="loads" />
        </Section>

        <Section title="Exported / outbound truck info" tint="#dcfce7"
                 right={diag.rowsInDb != null && (
                   <>
                     {diag.rowsInWindow} row{diag.rowsInWindow === 1 ? '' : 's'} in {diag.windowStart} to {diag.windowEnd}
                     {diag.rowsInWindow === 0 && diag.rowsInDb > 0 && (
                       <span style={{ color: '#b45309', marginLeft: '0.4rem' }}>
                         - {fmt(diag.rowsInDb)} total in DB; check date range
                       </span>
                     )}
                   </>
                 )}>
          <AutoRow label="Clay loads today" value={fmt(clay.dailyLoads)} unit="loads" />
          <AutoRow label="Clay loads MTD" value={fmt(clay.mtdLoads)}   unit="loads" />
          <AutoRow label="Clay tons today"  value={fmt2(clay.dailyTons)} unit="MT" />
          <AutoRow label="Clay tons MTD"  value={fmt2(clay.mtdTons)}   unit="MT" />
          <tr><td colSpan={3} style={{ borderTop: '1px solid #e5e7eb' }}></td></tr>
          <AutoRow label="Sand loads today" value={fmt(sand.dailyLoads)} unit="loads" />
          <AutoRow label="Sand loads MTD" value={fmt(sand.mtdLoads)}   unit="loads" />
          <AutoRow label="Sand tons today"  value={fmt2(sand.dailyTons)} unit="MT" />
          <AutoRow label="Sand tons MTD"  value={fmt2(sand.mtdTons)}   unit="MT" />
          <tr><td colSpan={3} style={{ borderTop: '1px solid #e5e7eb' }}></td></tr>
          <AutoRow label="Total tons today"        value={fmt2(totalTons.daily)}   unit="MT" />
          <AutoRow label="Total tons MTD"        value={fmt2(totalTons.mtd)}     unit="MT" />
          <AutoRow label="Outbound trucks today"   value={fmt(totalTrucks.daily)}   unit="loads" />
          <AutoRow label="Outbound trucks MTD" value={fmt(totalTrucks.monthly)} unit="loads" />
        </Section>
      </TwoUp>

      <TwoUp>
        <Section title="End-of-day plant readings" tint="#e0f2fe">
          <ManualInput label="Recirc tank level"         name="recirc_pct"                  type="num" unit="%"         manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Thickener torque"          name="thickener_torque_pct"        type="num" unit="%"         manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Sludge tank level"         name="sludge_pct"                  type="num" unit="%"         manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Process water tank level"  name="process_water_pct"           type="num" unit="%"         manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Coagulant tote in prod"    name="coagulant_prod_pct"          type="num" unit="%"         manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Warehouse coagulant totes" name="coagulant_warehouse_qty"     type="int" unit="totes"     manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Polymer storage tank"      name="polymer_tank_pct"            type="num" unit="%"         manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="Warehouse polymer bags (end of day)" name="polymer_warehouse_bags"      type="int" unit="bags 25kg"  manual={manual} setManual={setManual} disabled={locked} />
          <ManualInput label="New cationic polymer"      name="polymer_cationic_containers" type="int" unit="ctnrs 15kg" manual={manual} setManual={setManual} disabled={locked} />
        {tickets.balances.length === 0 ? (
          <tr><td colSpan={3} style={{ padding: '0.35rem 0.55rem', color: '#94a3b8', fontSize: '0.85em' }}>
            Uncomment the seed INSERTs in schema-019 (or add rows to <code>ticket_inventory</code>) to
            track hauler tickets here. Each row shows Starting minus consumed = Remaining.
          </td></tr>
        ) : tickets.balances.map((b, i) => {
          const key = b.hauler + '|' + (b.material || '');
          const low = b.remaining <= 5;
          return (
            <tr key={key + '|' + i}>
              <td style={{ padding: '0.28rem 0.55rem', color: '#0f172a' }}>
                {b.hauler}{b.material && <span className="small" style={{ color: '#64748b' }}> - {b.material}</span>}
                <div className="small" style={{ color: '#94a3b8' }}>
                  Since {b.starting_date}: started {b.starting_qty}, used {b.consumed}
                </div>
              </td>
              <td className="num" style={{ padding: '0.28rem 0.55rem', fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: low ? '#b91c1c' : '#0f172a' }}>
                {b.remaining}
              </td>
              <td style={{ padding: '0.28rem 0.55rem', color: '#94a3b8', fontSize: '0.8em' }}>
                tickets{low && ' - LOW'}
              </td>
            </tr>
          );
        })}
      </Section>
      </TwoUp>

      {equipmentList.length > 0 && (
        <TwoUp>
          <Section title="Equipment - hours today" tint="#f1f5f9">
            {equipmentList.filter(e => e.kind !== 'vehicles').map(e => e.metered ? (
              <MeterInput key={e.id} meterKey={e.code} label={e.display_name + ' (closing hour-meter reading)'} unit="hrs"
                          meters={data.meters} manual={manual} setManual={setManual} disabled={locked} />
            ) : (
              <EquipmentInput key={e.id} eq={e} unit="hrs"
                              manual={manual} setManual={setManual} disabled={locked} />
            ))}
            {equipmentList.filter(e => e.kind !== 'vehicles').length === 0 && (
              <tr><td colSpan={3} className="small" style={{ padding: '0.35rem 0.55rem', color: '#94a3b8' }}>
                No Equipment registered. Add on the Reference tab.
              </td></tr>
            )}
          </Section>

          <Section title="Vehicles - km today" tint="#eff6ff">
            {equipmentList.filter(e => e.kind === 'vehicles').map(e => e.metered ? (
              <MeterInput key={e.id} meterKey={e.code} label={e.display_name + ' (closing odometer)'} unit="km"
                          meters={data.meters} manual={manual} setManual={setManual} disabled={locked} />
            ) : (
              <EquipmentInput key={e.id} eq={e} unit="km"
                              manual={manual} setManual={setManual} disabled={locked} />
            ))}
            {equipmentList.filter(e => e.kind === 'vehicles').length === 0 && (
              <tr><td colSpan={3} className="small" style={{ padding: '0.35rem 0.55rem', color: '#94a3b8' }}>
                No Vehicles registered. Add on the Reference tab.
              </td></tr>
            )}
          </Section>
        </TwoUp>
      )}

      <Section title="Narrative" tint="#fef9c3">
        <RichNoteRow label="Purchasing / PO status" name="po_status"        manual={manual} setManual={setManual} disabled={locked} />
        <RichNoteRow label="Centrifuge notes"       name="centrifuge_notes" manual={manual} setManual={setManual} disabled={locked} />
        <RichNoteRow label="Other notes"            name="other_notes"      manual={manual} setManual={setManual} disabled={locked} />
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button className="refresh-btn" onClick={refresh} disabled={saving}>Reload</button>
        <button className="refresh-btn" onClick={() => save()} disabled={saving || locked}
                style={{ background: '#15803d', color: '#fff' }}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button className="refresh-btn" onClick={lockNoSend} disabled={saving || locked}
                style={{ background: '#334155', color: '#fff' }}
                title="Save and lock the report without sending an email.">
          Lock
        </button>
        <button className="refresh-btn" onClick={send} disabled={saving || locked}
                style={{ background: '#b45309', color: '#fff' }}
                title="Save, email PDF to recipients, and lock the report.">
          Send &amp; Lock
        </button>
      </div>

      <div className="note" style={{ marginTop: '0.8rem' }}>
        <strong>Sources:</strong> inbound from <code>tips_excel</code> + <code>tips_google</code>.
        Outbound from <code>loads_outgoing</code>. Manual inputs write to <code>daily_ops_reports</code>
        keyed on date. Save re-upserts the whole row; missing / blank fields become NULL.
      </div>

      {importOpen && (
        <ImportModal onClose={() => { setImportOpen(false); refreshHist(); refresh(); }} />
      )}
    </div>
  );
}

// ============================================================================
//  ImportModal -- upload monthly XLSX archives (one row per day).
//  Non-xlsx files (PDFs from the archive folder) are silently skipped.
// ============================================================================
function ImportModal({ onClose }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);

  async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const s = String(r.result || '');
        const b = s.split(',')[1] || '';
        resolve(b);
      };
      r.onerror = () => reject(new Error('read failed'));
      r.readAsDataURL(file);
    });
  }

  async function doImport() {
    if (files.length === 0) return;
    setUploading(true);
    const out = [];
    for (const f of files) {
      if (!/\.xlsx$/i.test(f.name)) {
        out.push({ filename: f.name, skipped: true, reason: 'not an xlsx file' });
        continue;
      }
      try {
        const b64 = await fileToBase64(f);
        const res = await fetch('/api/daily-report/import', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: f.name, base64: b64 })
        });
        const j = await res.json();
        out.push({ filename: f.name, ...j });
      } catch (e) {
        out.push({ filename: f.name, ok: false, error: e.message });
      }
      setResults([...out]);
    }
    setUploading(false);
  }

  const totals = results.reduce((a, r) => ({
    upserted: a.upserted + (Number(r.rowsUpserted) || 0),
    skipped: a.skipped + (Number(r.rowsSkipped) || 0),
    files: a.files + 1
  }), { upserted: 0, skipped: 0, files: 0 });

  return (
    <div className="modal-backdrop" onClick={() => !uploading && onClose()}>
      <div className="modal-card" onClick={e => e.stopPropagation()}
           style={{ minWidth: 380, maxWidth: 640, maxHeight: '85vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Import historical daily reports</h3>
        <p className="small" style={{ color: '#64748b' }}>
          Upload one or more monthly XLSX archives (one row per day). Each row is upserted
          into <code>daily_ops_reports</code>. PDF files in the archive folder are silently
          skipped -- only .xlsx files are processed.
        </p>
        <input type="file" accept=".xlsx" multiple disabled={uploading}
               onChange={e => { setFiles(Array.from(e.target.files || [])); setResults([]); }} />
        {files.length > 0 && (
          <div className="small" style={{ marginTop: '0.5rem', color: '#0f172a' }}>
            {files.length} file(s) selected: {files.map(f => f.name).join(', ')}
          </div>
        )}
        {results.length > 0 && (
          <>
            <hr style={{ margin: '0.75rem 0' }} />
            <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
              Progress: {totals.files}/{files.length} files -- {totals.upserted} rows upserted, {totals.skipped} skipped
            </div>
            <table style={{ width: '100%', fontSize: '0.88em' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>File</th>
                  <th style={{ textAlign: 'right' }}>Upserted</th>
                  <th style={{ textAlign: 'right' }}>Skipped</th>
                  <th style={{ textAlign: 'left' }}>Range / warnings</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td>{r.filename}</td>
                    <td style={{ textAlign: 'right' }}>{r.rowsUpserted || 0}</td>
                    <td style={{ textAlign: 'right', color: r.skipped ? '#94a3b8' : '#0f172a' }}>
                      {r.skipped ? 'file skipped' : (r.rowsSkipped || 0)}
                    </td>
                    <td className="small">
                      {r.error ? <span style={{ color: '#991b1b' }}>{r.error}</span>
                        : r.skipped ? r.reason || '-'
                        : (r.dateRange && r.dateRange.from) ? (r.dateRange.from + ' -> ' + r.dateRange.to) : '-'}
                      {r.warnings && r.warnings.length > 0 && (
                        <div style={{ color: '#c2410c' }}>
                          {r.warnings.length} warning(s)
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="refresh-btn" onClick={onClose} disabled={uploading} style={{ background: '#888' }}>
            {uploading ? 'Uploading...' : 'Close'}
          </button>
          <button className="refresh-btn" onClick={doImport}
                  disabled={uploading || files.length === 0} style={{ background: '#0369a1' }}>
            {uploading ? 'Importing...' : 'Import ' + files.length + ' file(s)'}
          </button>
        </div>
      </div>
    </div>
  );
}
